import { t } from '@if/core/i18n'
import { AnyError, getErrorMessage } from '@if/mui'
import { Alert, Button, CircularProgress, Stack } from '@mui/material'
import { useApplyCalendarSimulation, useRefreshSimulation } from '@src/api/loan/loan-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL } from '@src/types/constants'
import { CalendarSimulation, CalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { isAxiosError } from 'axios'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { BlockerFunction, useBlocker } from 'react-router-dom'
import PageContainer from '../Layout/PageContainer'
import RefetchSimulationAlert from './RefetchSimulationAlert'
import StaleSimulationAlert from './StaleSimulationAlert'

export enum LoanCalendarSimulationStatus {
  NoChanges = 'no_changes',
  HasChanges = 'has_changes',
  SavePending = 'save_pending',
}

export interface LoanCalendarSimulationState {
  originalSimulation: SignedCalendarSimulation
  currentSimulation: SignedCalendarSimulation
  actions: CalendarSimulationAction[]
  status: LoanCalendarSimulationStatus
}

interface LoanCalendarSimulationProviderProps {
  children?: ReactNode
  originalSignedSimulation: SignedCalendarSimulation
  refetch: () => void
}

// should never be exported. Only meant for internal state management
enum LoanCalendarSimulationStateActionType {
  Reset = 'reset',
  AddSimulationAction = 'add_simulation_action',
  SavePending = 'save_pending',
  SaveSuccess = 'save_success',
  SaveError = 'save_error',
}

interface BaseSimulationAction<T extends LoanCalendarSimulationStateActionType> {
  type: T
}

interface SaveSuccessSimulationAction extends BaseSimulationAction<LoanCalendarSimulationStateActionType.SaveSuccess> {
  payload: SignedCalendarSimulation
}

interface AddSimulationAction extends BaseSimulationAction<LoanCalendarSimulationStateActionType.AddSimulationAction> {
  payload: SignedCalendarSimulation
}

type SimulationAction =
  | AddSimulationAction
  | SaveSuccessSimulationAction
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.Reset>
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.SaveError>
  | BaseSimulationAction<LoanCalendarSimulationStateActionType.SavePending>

const SimulationReducer = (
  state: LoanCalendarSimulationState,
  action: SimulationAction,
): LoanCalendarSimulationState => {
  switch (action.type) {
    case LoanCalendarSimulationStateActionType.AddSimulationAction: {
      if (!action.payload) throw new Error('AddSimulationAction requires a payload')

      return {
        ...state,
        actions: action.payload.simulation.actions,
        currentSimulation: action.payload,
        status: LoanCalendarSimulationStatus.HasChanges,
      }
    }
    case LoanCalendarSimulationStateActionType.SavePending:
      return { ...state, status: LoanCalendarSimulationStatus.SavePending }
    case LoanCalendarSimulationStateActionType.Reset:
      return {
        ...state,
        actions: [],
        status: LoanCalendarSimulationStatus.NoChanges,
        currentSimulation: state.originalSimulation,
      }
    case LoanCalendarSimulationStateActionType.SaveSuccess:
      if (!action.payload) throw new Error('SaveSuccess requires a payload')
      return {
        actions: [],
        status: LoanCalendarSimulationStatus.NoChanges,
        originalSimulation: action.payload,
        currentSimulation: action.payload,
      }
    case LoanCalendarSimulationStateActionType.SaveError:
      return {
        ...state,
        status: LoanCalendarSimulationStatus.HasChanges,
      }
    default:
      return state
  }
}

export interface LoanCalendarSimulationContextProps extends LoanCalendarSimulationState {
  addSimulationAction: (action: SignedCalendarSimulation) => void
  refetch: () => void
}

const LoanCalendarSimulationContext = createContext<LoanCalendarSimulationContextProps | null>(null)

const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
  event.preventDefault()
  // Unfortunately, for security reasons,
  // modern browsers no longer support customizing
  // the text in the beforeunload confirmation dialog.
  event.returnValue = '' // This line is necessary for the dialog to appear in older browsers
  return ''
}

export const useLoanCalendarSimulation = () => {
  const context = useContext(LoanCalendarSimulationContext)
  if (!context) {
    throw new Error('useLoanCalendarSimulation must be used within a LoanCalendarSimulationProvider')
  }
  return context
}

const LoanCalendarSimulationProvider = ({
  children,
  originalSignedSimulation,
  refetch,
}: LoanCalendarSimulationProviderProps) => {
  const [simulationState, dispatch] = useReducer(SimulationReducer, {
    actions: [],
    originalSimulation: originalSignedSimulation,
    currentSimulation: originalSignedSimulation,
    status: LoanCalendarSimulationStatus.NoChanges,
  })
  const [showStaleAlert, setShowStaleAlert] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { showSuccess, showError } = useNotification()
  const [isInConflict, setIsInConflict] = useState(false)
  const loanId = originalSignedSimulation.simulation.fullLoan.loan.id
  const financingProgramId = originalSignedSimulation.simulation.fullLoan.loan.financingProgramId
  const { mutateAsync: applySimulation } = useApplyCalendarSimulation(loanId, financingProgramId)
  const { mutateAsync: refreshSimulation } = useRefreshSimulation(loanId, financingProgramId)

  // ------- REACT ROUTER BLOCKER -------
  const shouldBlockRouter = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (
        simulationState.status !== LoanCalendarSimulationStatus.NoChanges &&
        currentLocation.pathname !== nextLocation.pathname
      ) {
        return !window.confirm(t('common.editNotSavedLeave'))
      }
      return false
    },
    [simulationState.status],
  )
  useBlocker(shouldBlockRouter)

  // ------- NATIVE UNLOAD BLOCKER -------
  useEffect(() => {
    if (simulationState.status !== LoanCalendarSimulationStatus.NoChanges)
      window.addEventListener('beforeunload', beforeUnloadHandler)
    else window.removeEventListener('beforeunload', beforeUnloadHandler)

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [simulationState.status])

  // ------- AUTOMATIC STALE DETECTION -------
  useEffect(() => {
    if (simulationState.status !== LoanCalendarSimulationStatus.NoChanges) {
      const checkInterval = setInterval(() => {
        if (simulationState.currentSimulation.simulation.isExpired()) {
          setShowStaleAlert(true)
        }
      }, SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL)
      return () => clearInterval(checkInterval)
    }
  }, [simulationState.status, simulationState.currentSimulation.simulation])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      // IMPORTANT: Backend will fetch fresh loan data from DB and reapply actions
      // We send current actions list - backend fetches fresh loan state and reapplies them
      const currentSimulationDto = CalendarSimulation.toDto(simulationState.currentSimulation.simulation)

      const simulationToRefresh: SignedCalendarSimulation = {
        signature: '', // Not validated in RefreshSimulation endpoint
        simulation: CalendarSimulation.fromDto({
          ...currentSimulationDto,
          actions: currentSimulationDto.actions, // Only actions matter - backend fetches fresh FullLoan
        }),
      }

      const refreshedSimulation = await refreshSimulation(simulationToRefresh)

      // Update the current simulation with the refreshed data
      dispatch({ type: LoanCalendarSimulationStateActionType.AddSimulationAction, payload: refreshedSimulation })

      // Close the alert
      setShowStaleAlert(false)
    } catch (error: unknown) {
      showError(getErrorMessage(error as AnyError))
    } finally {
      setIsRefreshing(false)
    }
  }

  const saveHandler = async () => {
    if (simulationState.status !== LoanCalendarSimulationStatus.HasChanges)
      throw new Error('Save requires a currentSimulationWithSignature')

    // Check if simulation has expired
    if (simulationState.currentSimulation.simulation.isExpired()) {
      // Show alert and let user decide what to do
      setShowStaleAlert(true)
      return
    }

    dispatch({ type: LoanCalendarSimulationStateActionType.SavePending })

    try {
      // Apply the simulation
      const result = await applySimulation(simulationState.currentSimulation)
      dispatch({ type: LoanCalendarSimulationStateActionType.SaveSuccess, payload: result })
      showSuccess()
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 409) {
        setIsInConflict(true)
      } else {
        dispatch({ type: LoanCalendarSimulationStateActionType.SaveError })
        showError(getErrorMessage(error as AnyError))
      }
    }
  }

  const handleRefetch = () => {
    setIsInConflict(false)
    refetch()
  }

  const ctxValue = useMemo<LoanCalendarSimulationContextProps>(
    () => ({
      ...simulationState,
      addSimulationAction: (action: SignedCalendarSimulation) => {
        dispatch({ type: LoanCalendarSimulationStateActionType.AddSimulationAction, payload: action })
      },
      refetch,
    }),
    [simulationState, refetch],
  )
  const isLoading = simulationState.status === LoanCalendarSimulationStatus.SavePending

  return (
    <LoanCalendarSimulationContext.Provider value={ctxValue}>
      {isInConflict && <RefetchSimulationAlert refetch={handleRefetch} />}
      <StaleSimulationAlert
        open={showStaleAlert}
        onCancel={() => setShowStaleAlert(false)}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
      {simulationState.status !== LoanCalendarSimulationStatus.NoChanges && (
        <PageContainer disableBottomGutter sx={{ mb: 2 }}>
          <Alert
            icon={isLoading ? <CircularProgress size={20} /> : undefined}
            severity="info"
            action={
              <Stack direction="row" spacing={0.5}>
                <Button
                  disabled={isLoading}
                  color="inherit"
                  onClick={() => dispatch({ type: LoanCalendarSimulationStateActionType.Reset })}
                >
                  {t('common.cancel')}
                </Button>
                <Button loading={isLoading} color="info" onClick={saveHandler}>
                  {t('common.save')}
                </Button>
              </Stack>
            }
          >
            {isLoading ? t('common.savePending') : t('common.editNotSaved')}
          </Alert>
        </PageContainer>
      )}
      {children}
    </LoanCalendarSimulationContext.Provider>
  )
}

export default LoanCalendarSimulationProvider
